import { CarrierShipmentForm } from './carrier-shipment-form'
import type { CarrierReceiverValue } from '@/components/carrier/carrier-receiver-fields'
import type { CarrierQuoteContext } from '@/lib/carrier-quote-fingerprint'
import { useSalesOrder } from '@/hooks/use-sales-order-query'
import { canEditOrderInfo } from '@/lib/order-edit'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { m } from '@/lib/paraglide/messages'

interface Props {
    open: boolean
    onOpenChange: (open: boolean) => void
    orderId: string
    quoteContext: CarrierQuoteContext
}

export function CarrierDispatchDialog({
    open,
    onOpenChange,
    orderId,
    quoteContext,
}: Props) {
    const { data: order } = useSalesOrder(orderId)
    const gift = order?.gift
    const initialShippingFeePayer =
        order?.shipping_fee_payer === 'customer' ? 'customer' : 'company'
    // The receiver block mirrors the order; the order's own edit rules say
    // whether it may still change (not out for delivery, not paid).
    const receiver: CarrierReceiverValue | undefined = order
        ? {
              name: order.customer_name,
              phone: order.customer_phone,
              address: order.shipping_address,
              provinceCode: order.shipping_province_code ?? undefined,
              provinceName: order.shipping_province ?? undefined,
              wardCode: order.shipping_ward_code ?? undefined,
              wardName: order.shipping_ward ?? undefined,
          }
        : undefined
    const customerServiceCode = order?.customer_shipping_service_code
    const customerService = customerServiceCode
        ? {
              code: customerServiceCode,
              name:
                  order?.customer_shipping_service_name || customerServiceCode,
          }
        : undefined
    const receiverEditable = order
        ? canEditOrderInfo(order.status, order.payment_status)
        : false

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {/* The booking form grew past a laptop viewport (services, the
                inspection fee, its hint and errors), so the dialog scrolls its
                own body instead of pushing the submit button off screen. */}
            <DialogContent className="max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{m.carrier_form_title()}</DialogTitle>
                    <DialogDescription>
                        {m.carrier_form_weight_hint()}
                    </DialogDescription>
                </DialogHeader>
                <CarrierShipmentForm
                    orderId={orderId}
                    active={open}
                    quoteContext={quoteContext}
                    initialShippingFeePayer={initialShippingFeePayer}
                    receiver={receiver}
                    receiverEditable={receiverEditable}
                    prepaidShippingFee={
                        gift?.shipping_prepaid ? gift.shipping_fee : undefined
                    }
                    customerService={customerService}
                    onCreated={() => onOpenChange(false)}
                />
            </DialogContent>
        </Dialog>
    )
}
